/**
 * Category Configuration - Story 7.1: Create Marketplace Homepage
 * Comprehensive metadata and mappings for product categories
 *
 * This configuration provides:
 * - Visual styling (icons, colors)
 * - Display labels and descriptions
 * - Accessibility information
 * - Category-specific metadata
 */

import {
  Wind,
  Leaf,
  Sprout,
  Wrench,
  Droplet,
  Grid3x3,
  type LucideIcon,
} from 'lucide-react';
import type { ProductCategory } from '../types';

/**
 * Category Metadata Interface
 * Complete configuration for each product category
 */
export interface CategoryMetadata {
  /** Category identifier */
  key: ProductCategory;
  /** Lucide React icon component */
  icon: LucideIcon;
  /** Display label for UI */
  label: string;
  /** Detailed description for tooltips/accessibility */
  description: string;
  /** Tailwind color classes for text */
  color: string;
  /** Tailwind color classes for background (hover/active states) */
  bgColor: string;
  /** Tailwind color classes for badge */
  badgeColor: string;
  /** Tailwind color classes for badge background */
  badgeBgColor: string;
  /** ARIA label for screen readers */
  ariaLabel: string;
  /** Sort order for display */
  order: number;
}

/**
 * All Category Keys
 * TypeScript-safe array of all category identifiers
 */
export const CATEGORY_KEYS: ProductCategory[] = [
  'BIOGAS',
  'BIOMASS',
  'SEEDS',
  'EQUIPMENT',
  'FERTILIZER',
  'OTHER',
];

/**
 * Category Configuration Map
 * Complete metadata for all product categories
 *
 * Design Notes:
 * - Icons chosen for semantic meaning and visual clarity
 * - Colors follow consistent design system (green for natural/organic, blue for technical)
 * - Descriptions optimized for accessibility and user understanding
 * - Order reflects typical user browsing priority
 */
export const CATEGORY_CONFIG: Record<ProductCategory, CategoryMetadata> = {
  BIOGAS: {
    key: 'BIOGAS',
    icon: Wind,
    label: 'Biogas',
    description: 'Biogas products, solutions, and related equipment for renewable energy',
    color: 'text-green-700',
    bgColor: 'bg-green-50',
    badgeColor: 'text-green-700',
    badgeBgColor: 'bg-green-100',
    ariaLabel: 'Filter by Biogas products',
    order: 1,
  },
  BIOMASS: {
    key: 'BIOMASS',
    icon: Leaf,
    label: 'Biomass',
    description: 'Biomass materials, organic waste, and bio-fuel products',
    color: 'text-amber-700',
    bgColor: 'bg-amber-50',
    badgeColor: 'text-amber-700',
    badgeBgColor: 'bg-amber-100',
    ariaLabel: 'Filter by Biomass products',
    order: 2,
  },
  SEEDS: {
    key: 'SEEDS',
    icon: Sprout,
    label: 'Seeds',
    description: 'Agricultural seeds, saplings, and planting materials',
    color: 'text-emerald-700',
    bgColor: 'bg-emerald-50',
    badgeColor: 'text-emerald-700',
    badgeBgColor: 'bg-emerald-100',
    ariaLabel: 'Filter by Seeds and planting materials',
    order: 3,
  },
  EQUIPMENT: {
    key: 'EQUIPMENT',
    icon: Wrench,
    label: 'Equipment',
    description: 'Agricultural equipment, machinery, and technical tools',
    color: 'text-blue-700',
    bgColor: 'bg-blue-50',
    badgeColor: 'text-blue-700',
    badgeBgColor: 'bg-blue-100',
    ariaLabel: 'Filter by Equipment and machinery',
    order: 4,
  },
  FERTILIZER: {
    key: 'FERTILIZER',
    icon: Droplet,
    label: 'Fertilizer',
    description: 'Organic fertilizers, soil enhancers, and nutrient solutions',
    color: 'text-purple-700',
    bgColor: 'bg-purple-50',
    badgeColor: 'text-purple-700',
    badgeBgColor: 'bg-purple-100',
    ariaLabel: 'Filter by Fertilizer and soil products',
    order: 5,
  },
  OTHER: {
    key: 'OTHER',
    icon: Grid3x3,
    label: 'Other',
    description: 'Other agricultural and biogas-related products',
    color: 'text-gray-700',
    bgColor: 'bg-gray-50',
    badgeColor: 'text-gray-700',
    badgeBgColor: 'bg-gray-100',
    ariaLabel: 'Filter by Other products',
    order: 6,
  },
};

/**
 * Get Category Metadata
 * Type-safe retrieval of category configuration
 *
 * @param category - ProductCategory key
 * @returns CategoryMetadata for the specified category
 *
 * @example
 * const biogasConfig = getCategoryMetadata('BIOGAS');
 * console.log(biogasConfig.label); // "Biogas"
 */
export const getCategoryMetadata = (category: ProductCategory): CategoryMetadata => {
  return CATEGORY_CONFIG[category];
};

/**
 * Get All Categories
 * Returns array of all category metadata, sorted by order
 *
 * @returns Array of CategoryMetadata sorted by display order
 *
 * @example
 * const categories = getAllCategories();
 * categories.forEach(cat => console.log(cat.label));
 */
export const getAllCategories = (): CategoryMetadata[] => {
  return CATEGORY_KEYS.map((key) => CATEGORY_CONFIG[key]).sort((a, b) => a.order - b.order);
};

/**
 * Get Category Count Text
 * Formats product count for display
 *
 * @param count - Number of products in category
 * @returns Formatted count string
 *
 * @example
 * getCategoryCountText(0); // "No products"
 * getCategoryCountText(1); // "1 product"
 * getCategoryCountText(42); // "42 products"
 */
export const getCategoryCountText = (count: number): string => {
  if (count === 0) return 'No products';
  if (count === 1) return '1 product';
  return `${count.toLocaleString('en-IN')} products`;
};

/**
 * Category Display Options
 * Configuration for CategoryFilter component variants
 */
export interface CategoryDisplayOptions {
  /** Show product count badges */
  showCount: boolean;
  /** Show category descriptions as tooltips */
  showTooltips: boolean;
  /** Display mode: grid or inline */
  displayMode: 'grid' | 'inline';
  /** Button size variant */
  buttonSize: 'sm' | 'default' | 'lg';
  /** Show "View All" option */
  showViewAll: boolean;
}

/**
 * Default Display Options
 * Default configuration for CategoryFilter component
 */
export const DEFAULT_DISPLAY_OPTIONS: CategoryDisplayOptions = {
  showCount: true,
  showTooltips: true,
  displayMode: 'inline',
  buttonSize: 'default',
  showViewAll: true,
};
